// WebSocket Service for Real-time Mining Data

import { MinerStatus, AlgorithmProfitability } from '../types';

export interface WebSocketMessage {
  type: 'miners_update' | 'profitability_update' | 'pool_update' | 'system_alert';
  data: any;
  timestamp: number;
}

class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 5000;
  private messageHandlers: Map<string, (data: any) => void> = new Map();
  private connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error' = 'disconnected';

  constructor(private url: string) {}

  // Connect to WebSocket server
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.connectionStatus = 'connecting';
        this.ws = new WebSocket(this.url);

        this.ws.onopen = () => {
          console.log('WebSocket connected');
          this.connectionStatus = 'connected';
          this.reconnectAttempts = 0;
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };

        this.ws.onclose = () => {
          console.log('WebSocket disconnected');
          this.connectionStatus = 'disconnected';
          this.attemptReconnect();
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.connectionStatus = 'error';
          reject(error);
        };

      } catch (error) {
        this.connectionStatus = 'error';
        reject(error);
      }
    });
  }

  // Disconnect from WebSocket
  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.connectionStatus = 'disconnected';
  }

  // Send message to server
  send(message: any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected');
    }
  }

  // Subscribe to specific message types
  subscribe(messageType: string, handler: (data: any) => void) {
    this.messageHandlers.set(messageType, handler);
  }

  // Unsubscribe from message type
  unsubscribe(messageType: string) {
    this.messageHandlers.delete(messageType);
  }

  // Get connection status
  getStatus() {
    return this.connectionStatus;
  }

  // Handle incoming messages
  private handleMessage(message: WebSocketMessage) {
    const handler = this.messageHandlers.get(message.type);
    if (handler) {
      handler(message.data);
    }

    // Log message for debugging
    console.log(`WebSocket message [${message.type}]:`, message.data);
  }

  // Attempt to reconnect
  private attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      
      setTimeout(() => {
        this.connect().catch(error => {
          console.error('Reconnection failed:', error);
        });
      }, this.reconnectInterval);
    } else {
      console.error('Max reconnection attempts reached');
    }
  }
}

// Create WebSocket service instance
const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:3001/ws';
export const wsService = new WebSocketService(WS_URL);

// Specialized WebSocket handlers for mining data
export const miningWebSocket = {
  // Initialize WebSocket connection with mining-specific handlers
  initialize: async () => {
    try {
      await wsService.connect();
      
      // Subscribe to mining-specific message types
      wsService.subscribe('miners_update', (data: MinerStatus[]) => {
        // Handle miner status updates
        console.log('Miners updated:', data);
      });

      wsService.subscribe('profitability_update', (data: AlgorithmProfitability[]) => {
        // Handle profitability updates
        console.log('Profitability updated:', data);
      });

      wsService.subscribe('pool_update', (data: any) => {
        // Handle pool statistics updates
        console.log('Pool stats updated:', data);
      });

      wsService.subscribe('system_alert', (data: any) => {
        // Handle system alerts (miner offline, temperature warnings, etc.)
        console.log('System alert:', data);
      });

    } catch (error) {
      console.error('Failed to initialize WebSocket:', error);
    }
  },

  // Request real-time miner data
  requestMinerData: () => {
    wsService.send({
      type: 'request_miners',
      timestamp: Date.now()
    });
  },

  // Request profitability updates
  requestProfitabilityData: () => {
    wsService.send({
      type: 'request_profitability',
      timestamp: Date.now()
    });
  },

  // Request pool statistics
  requestPoolData: () => {
    wsService.send({
      type: 'request_pools',
      timestamp: Date.now()
    });
  },

  // Subscribe to specific miner updates
  subscribeMiner: (minerId: string) => {
    wsService.send({
      type: 'subscribe_miner',
      minerId,
      timestamp: Date.now()
    });
  },

  // Unsubscribe from miner updates
  unsubscribeMiner: (minerId: string) => {
    wsService.send({
      type: 'unsubscribe_miner',
      minerId,
      timestamp: Date.now()
    });
  },

  // Get connection status
  getConnectionStatus: () => wsService.getStatus(),

  // Disconnect
  disconnect: () => wsService.disconnect()
};

// Mock WebSocket server simulation (for development)
export const mockWebSocketServer = {
  start: () => {
    console.log('Starting mock WebSocket server simulation...');
    
    // Simulate periodic miner updates
    setInterval(() => {
      const mockMiners: MinerStatus[] = [
        {
          id: 'miner_001',
          name: 'Antminer S19 Pro',
          status: Math.random() > 0.1 ? 'online' : 'offline',
          hashrate: 110000000000 + (Math.random() * 10000000000),
          difficulty: 5000000 + (Math.random() * 500000),
          algorithm: 'SHA256',
          pool: 'f2pool',
          nowMining:'Unknown',
          lastSeen: Date.now(),
          temperature: 60 + (Math.random() * 15),
          power: 3250 + (Math.random() * 200)
        }
      ];

      // Simulate WebSocket message
      const message: WebSocketMessage = {
        type: 'miners_update',
        data: mockMiners,
        timestamp: Date.now()
      };

      console.log('Mock WebSocket message:', message);
    }, 10000); // Every 10 seconds

    // Simulate profitability updates
    setInterval(() => {
      const mockProfitability: AlgorithmProfitability[] = [
        {
          timestamp: Date.now(),
          profitability: 0.085 + (Math.random() * 0.02),
          algorithm: 'SHA256',
          over_average: Math.random() * 0.1 - 0.05,
          boosted: Math.random() > 0.7
        }
      ];

      const message: WebSocketMessage = {
        type: 'profitability_update',
        data: mockProfitability,
        timestamp: Date.now()
      };

      console.log('Mock profitability update:', message);
    }, 30000); // Every 30 seconds
  }
};

export default wsService;